import * as O from 'fp-ts/lib/Option';
import React, {ComponentType, FC} from 'react';
import {
  batch as reactReduxBatch,
  ReactReduxContext,
  ResolveThunks,
  // eslint-disable-next-line no-restricted-imports
  useDispatch,
  // eslint-disable-next-line no-restricted-imports
  useSelector,
} from 'react-redux';
import {Action, compose, StoreEnhancer} from 'redux';
import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {Subtract} from 'utility-types';

import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {HttpError} from '../services/http/httpError';
import {AppStore} from '../store/appStore';
import {AppParametricSelector, AppSelector} from './reselectHelpers';

/*
 * Simple action with payload.
 */

export interface ActionBase extends Action<string> {
  readonly type: string;
}

export interface ActionWithPayload<T> extends ActionBase {
  readonly payload: T;
}

/*
 * Map Dispatch to Props.
 */

type ActionCreator<T, U extends any[]> = (...args: U) => T | ThunkAction<T, any, any, any>;

export type WrappedActionCreator<F extends ActionCreator<any, any>> = F extends ActionCreator<
  infer T,
  infer U
>
  ? (...args: U) => T
  : never;

/**
 * @deprecated If you need access to the props, use the makeMapDispatchToProps helper instead.
 * Otherwise, a plain object of actions will suffice.
 * @see makeMapDispatchToProps
 */
export function wrapActionCreator<T, U extends any[]>(
  dispatch: ThunkDispatch<any, any, any>,
  actionCreator: ActionCreator<T, U>,
): (...args: U) => T {
  return (...args) => dispatch(actionCreator(...args) as any);
}

/** Find the resulting dispatch props from a MapDispatchToProps method or DispatchProps object. */
export type ResolveDispatchProps<TDispatchProps> = TDispatchProps extends (...args: any[]) => any
  ? ReturnType<TDispatchProps>
  : ResolveThunks<TDispatchProps>;

/*
 * Wrapper for catching promise rejections in async dispatches.
 */

export function safeAsyncDispatch(dispatchPromise: Promise<void>): Promise<void> {
  return dispatchPromise.catch((error) => {
    if (error instanceof HttpError) {
      return;
    }

    reportToBugsnag(error); // Report non-HTTP errors to Bugsnag.
  });
}

/*
 * Enhancers.
 */

export function composeEnhancers(): StoreEnhancer;
export function composeEnhancers<Ext1, StateExt1>(
  enhancer1: StoreEnhancer<Ext1, StateExt1>,
): StoreEnhancer<Ext1, StateExt1>;
export function composeEnhancers<Ext1, StateExt1, Ext2, StateExt2>(
  enhancer1: StoreEnhancer<Ext1, StateExt1>,
  enhancer2: StoreEnhancer<Ext2, StateExt2>,
): StoreEnhancer<Ext1 & Ext2, StateExt1 & StateExt2>;
export function composeEnhancers<Ext1, StateExt1, Ext2, StateExt2, Ext3, StateExt3>(
  enhancer1: StoreEnhancer<Ext1, StateExt1>,
  enhancer2: StoreEnhancer<Ext2, StateExt2>,
  enhancer3: StoreEnhancer<Ext3, StateExt3>,
): StoreEnhancer<Ext1 & Ext2 & Ext3, StateExt1 & StateExt2 & StateExt3>;
export function composeEnhancers<Ext1, StateExt1, Ext2, StateExt2, Ext3, StateExt3, Ext4, StateExt4>(
  enhancer1: StoreEnhancer<Ext1, StateExt1>,
  enhancer2: StoreEnhancer<Ext2, StateExt2>,
  enhancer3: StoreEnhancer<Ext3, StateExt3>,
  enhancer4: StoreEnhancer<Ext4, StateExt4>,
): StoreEnhancer<Ext1 & Ext2 & Ext3 & Ext4, StateExt1 & StateExt2 & StateExt3 & StateExt4>;
export function composeEnhancers<
  Ext1,
  StateExt1,
  Ext2,
  StateExt2,
  Ext3,
  StateExt3,
  Ext4,
  StateExt4,
  Ext5,
  StateExt5,
>(
  enhancer1: StoreEnhancer<Ext1, StateExt1>,
  enhancer2: StoreEnhancer<Ext2, StateExt2>,
  enhancer3: StoreEnhancer<Ext3, StateExt3>,
  enhancer4: StoreEnhancer<Ext4, StateExt4>,
  enhancer5: StoreEnhancer<Ext5, StateExt5>,
): StoreEnhancer<Ext1 & Ext2 & Ext3 & Ext4 & Ext5, StateExt1 & StateExt2 & StateExt3 & StateExt4 & StateExt5>;
export function composeEnhancers(...enhancers: StoreEnhancer<any, any>[]): StoreEnhancer<any, any>;
export function composeEnhancers(...enhancers: StoreEnhancer[]): StoreEnhancer {
  return compose(...enhancers);
}

/*
 * React.
 */

/**
 * Variation of react-redux batch() that passes through its return value.
 * TODO: Deprecate this in favor of batchStore.
 */

export function batch<R>(callback: () => R): R {
  let result: O.Option<R> = O.none;

  reactReduxBatch(() => {
    result = O.some(callback());
  });

  if (O.isNone(result)) {
    throw new Error('React batch callback not called synchronously');
  }

  return result.value;
}

export const useAppDispatch = () => useDispatch<AppStore['dispatch']>();

export function useAppSelector<T>(
  selector: AppSelector<T> | AppParametricSelector<void, T>,
  equalityFn?: (left: T, right: T) => boolean,
): T {
  return useSelector(selector, equalityFn);
}

/*
 * Store Access.
 */

export interface StoreProps {
  store: AppStore;
}

export function withStore<T extends StoreProps>(Component: ComponentType<T>): FC<Subtract<T, StoreProps>> {
  return function (props) {
    return (
      <ReactReduxContext.Consumer>
        {(context) => <Component {...(props as any)} store={context.store} />}
      </ReactReduxContext.Consumer>
    );
  };
}
